import classnames from 'classnames';
import { noop } from 'ramda-adjunct';
import React, { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { stopPropagation } from '../../../../utils/dom/domEvents';
import { Checkbox, FolderThumbnail, IconButton, Input, Text, Tooltip, WixIcons } from '../../../common';
import { GenericItem } from '../../Generic/GenericItem';
import styles from './SmallListFolder.scss';
import { IOuter } from './SmallListFolder.types';
import { isWixSiteFolder } from '../../../../utils/media-manager-sdk/items';
import { useIsCurrentSite } from '../../../../utils/hooks/useIsCurrentSite';

const emptySelectedItems = [];
export const SmallListFolder: FC<IOuter> = ({
  item,
  className,
  selected = false,
  onClick = noop,
  isEditing,
  showCheckbox,
  checkboxProps = {},
  onNameChange,
  onRenameComplete,
  onRenameCancel,
  withoutChevron = false,
}) => {
  const { isCurrentSite } = useIsCurrentSite(item);
  const { formatMessage } = useIntl();

  const handleRightArrowClick = useCallback(
    (event) => {
      stopPropagation(event);
      onClick(item, event);
    },
    [onClick, item],
  );

  return (
    <GenericItem
      item={item}
      onNameChange={onNameChange}
      onRenameComplete={onRenameComplete}
      onRenameCancel={onRenameCancel}
      onClick={showCheckbox ? checkboxProps.onChange : onClick}
      noContextMenu
      selectedItems={emptySelectedItems}
    >
      {({ handleNameChange, handleRenameCancel, handleRenameComplete }) => (
        <div
          className={classnames(styles.folder, selected && styles.selected, className)}
          data-hook="small-list-folder"
        >
          {showCheckbox && (
            <div className={styles.checkboxWrapper}>
              <Checkbox checked={checkboxProps.checked} />
            </div>
          )}

          <div className={styles.thumbnailWrapper}>
            <FolderThumbnail className={styles.thumbnail} folder={item} small />
          </div>

          <Text weight="normal" size="small" tagName="div" className={styles.nameWrapper}>
            {isEditing ? (
              <Input
                value={item.name}
                onChange={handleNameChange}
                onBlur={handleRenameCancel}
                onEnterPressed={handleRenameComplete}
                onEscapePressed={handleRenameCancel}
                autoFocus
                dataHook="small-list-folder-name-edit-input"
              />
            ) : (
              <span data-hook="small-list-folder-name">
                {isWixSiteFolder(item) && isCurrentSite
                  ? formatMessage(
                      {
                        id: 'files-source.label.current-site-name',
                      },
                      {
                        siteName: item.name,
                        currentSite: formatMessage({ id: 'files-source.label.current_site' }),
                      },
                    )
                  : item.name}
              </span>
            )}
          </Text>

          {!withoutChevron && !isEditing && (
            <div className={styles.actions}>
              <Tooltip content={<FormattedMessage id="modal-files-manager.go-to-folder" />}>
                <IconButton
                  skin="light"
                  size="small"
                  className={styles.button}
                  onClick={showCheckbox ? handleRightArrowClick : noop}
                >
                  <WixIcons.ChevronRightSmall />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      )}
    </GenericItem>
  );
};
